import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { useAuth } from './auth/AuthProvider'
import { CalendarPage } from './pages/CalendarPage'
import { MapPage } from './pages/MapPage'
import { InstrumentsPage } from './pages/InstrumentsPage'
import { CustomerDetailPage } from './pages/CustomerDetailPage'
import { CustomersPage } from './pages/CustomersPage'
import { DeviceDetailPage } from './pages/DeviceDetailPage'
import { DeviceLabelPage } from './pages/DeviceLabelPage'
import { DevicesPage } from './pages/DevicesPage'
import { DeviceCodeLandingPage, ScanPage } from './pages/ScanPage'
import { ServiceVisitPage } from './pages/ServiceVisitPage'
import { HomePage } from './pages/HomePage'
import { QuickNotePage } from './pages/QuickNotePage'
import { LoginPage } from './pages/LoginPage'
import { MorePage } from './pages/MorePage'
import { NotificationsPage } from './pages/NotificationsPage'
import { ProjectDetailPage } from './pages/ProjectDetailPage'
import { ProjectAccessCodesPage } from './pages/ProjectAccessCodesPage'
import { CaseCodeLandingPage, ProjectLabelPage } from './pages/ProjectLabelPage'
import { GuidePage } from './guide/GuidePage'
import { ProjectsPage } from './pages/ProjectsPage'
import { TasksPage } from './pages/TasksPage'
import { VpnProfilePage } from './pages/VpnProfilePage'
import { ActivityPage } from './pages/admin/ActivityPage'
import { ModulesPage } from './pages/admin/ModulesPage'
import { AbsencesPage } from './pages/AbsencesPage'
import { CertificatesPage } from './pages/CertificatesPage'
import { AccessKeysPage } from './pages/AccessKeysPage'
import { EquipmentCodeLandingPage, EquipmentDetailPage } from './pages/EquipmentDetailPage'
import { EquipmentLabelPage } from './pages/EquipmentLabelPage'
import { EquipmentPage } from './pages/EquipmentPage'
import { VehicleDetailPage, VehiclesPage } from './pages/VehiclesPage'
import { WarrantiesPage } from './pages/WarrantiesPage'
import { ManualsPage } from './pages/ManualsPage'
import { IntranetEventPage, IntranetPage, IntranetPostPage } from './pages/IntranetPage'
import { IntranetMeetingPage } from './components/IntranetMeetings'
import { MusPage } from './components/IntranetMus'
import { ApprenticePage } from './components/IntranetApprentices'
import { SelfCheckPage } from './components/SelfChecks'
import { ReceiptsPage } from './pages/ReceiptsPage'
import { PriceImportPage } from './pages/admin/PriceImportPage'
import { SupportMailPage } from './pages/admin/SupportMailPage'
import { SupportPage, SupportTicketPage } from './pages/SupportPage'
import { StockFieldPage } from './pages/StockFieldPage'
import { VanOrderDetailPage, VanOrdersPage } from './pages/VanOrdersPage'
import { StockItemsPage, StockItemLandingPage } from './pages/StockItemsPage'
import {
  StockLocationsPage,
  StockLocationDetailPage,
  StockLocationLandingPage
} from './pages/StockLocationsPage'
import { EquipmentCategoriesPage } from './pages/admin/EquipmentCategoriesPage'
import { ManualCategoriesPage } from './pages/admin/ManualCategoriesPage'
import { TeamsPage } from './pages/admin/TeamsPage'
import { ManualDetailPage } from './pages/ManualDetailPage'
import { ManualEditorPage } from './pages/ManualEditorPage'
import { FieldMessagesPage } from './pages/FieldMessagesPage'
import { OperationsPage } from './pages/admin/OperationsPage'
import { DashboardsPage } from './pages/admin/DashboardsPage'
import { DeadlineAlertsPage } from './pages/admin/DeadlineAlertsPage'
import { Microsoft365Page } from './pages/admin/Microsoft365Page'
import { MessagesPage } from './pages/admin/MessagesPage'
import { SmsPage } from './pages/admin/SmsPage'
import { OrdrestyringPage } from './pages/admin/OrdrestyringPage'
import { VpnPage } from './pages/admin/VpnPage'
import { VpnLabelPage } from './pages/admin/VpnLabelPage'
import { DeviceTypesPage } from './pages/admin/DeviceTypesPage'
import { ServiceChecklistsPage } from './pages/admin/ServiceChecklistsPage'
import { SubscriptionsPage } from './pages/admin/SubscriptionsPage'
import { ScreensPage } from './pages/admin/ScreensPage'
import { StatusesPage } from './pages/admin/StatusesPage'
import { TemplatesPage } from './pages/admin/TemplatesPage'
import { DiagramsPage } from './pages/DiagramsPage'
import { DiagramEditorPage } from './pages/DiagramEditorPage'
import { UsersPage } from './pages/admin/UsersPage'
import { InvoicingPage } from './pages/InvoicingPage'

/** Ruter for bruger- og adminpanelet. TV-visningen ligger uden for denne router. */
export function App() {
  const { isAuthenticated, isLoading, isAdmin, isProjectManager, hasRole, canUseVpn } = useAuth()

  if (isLoading) {
    return (
      <div className="login">
        <div className="login__card">
          <img className="login__logo" src="/logo/tc-logo.svg" alt="theodor christiansen" />
          <p className="muted">Logger ind ...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <LoginPage />
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<HomePage />} />
          {/* Hurtigknappen i marken (beslutning 082): note og foto paa den sag,
              montoeren staar paa. */}
          <Route path="quick-note" element={<QuickNotePage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="map" element={<MapPage />} />
          <Route path="instruments" element={<InstrumentsPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          {/* Sager klar til faktura - kun kontoret (beslutning 147). */}
          <Route path="invoicing" element={<InvoicingPage />} />
          <Route path="projects/:id" element={<ProjectDetailPage />} />
          {/* Porte og nøglebokse med koder (beslutning 088). */}
          <Route path="projects/:id/access-codes" element={<ProjectAccessCodesPage />} />
          <Route path="projects/:id/label" element={<ProjectLabelPage />} />
          {/* Sagskassens QR-kode (beslutning 143) - sin egen sti, ikke /e/, /l/, /v/ eller /m/. */}
          <Route path="s/:id" element={<CaseCodeLandingPage />} />
          <Route path="s/:id/:box" element={<CaseCodeLandingPage />} />
          {/* Vejledningen for alle roller - den viser selv kun det, rollen kan (beslutning 089). */}
          <Route path="guide" element={<GuidePage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="customers/:id" element={<CustomerDetailPage />} />

          {/* Service og enheder. /e/:code er den adresse, QR-mærkaterne peger på. */}
          <Route path="scan" element={<ScanPage />} />
          <Route path="devices" element={<DevicesPage />} />
          <Route path="devices/label" element={<DeviceLabelPage />} />
          <Route path="devices/:id" element={<DeviceDetailPage />} />
          <Route path="devices/:id/label" element={<DeviceLabelPage />} />
          <Route path="e/:code" element={<DeviceCodeLandingPage />} />
          <Route path="service-visits/:id" element={<ServiceVisitPage />} />
          {/* Markens side (beslutning 071): scan flere ting i traek og
              registrér dem paa én gang. */}
          <Route path="stock/scan" element={<StockFieldPage />} />
          <Route path="stock/orders" element={<VanOrdersPage />} />
          <Route path="stock/orders/:id" element={<VanOrderDetailPage />} />
          <Route path="stock/items" element={<StockItemsPage />} />
          <Route path="stock/locations" element={<StockLocationsPage />} />
          <Route path="stock/locations/:id" element={<StockLocationDetailPage />} />
          {/* Adressen QR-maerkaterne paa kasser og biler peger paa. */}
          <Route path="l/:code" element={<StockLocationLandingPage />} />
          {/* Adressen QR-maerkaterne paa varer peger paa. */}
          <Route path="v/:code" element={<StockItemLandingPage />} />
          {/* Manualer (beslutning 063). ALLE maa laese - en manual, montoeren
              ikke kan komme til, bliver ikke fulgt. Skriverettighederne
              haandhaeves i API-et og skjuler knapperne i brugerfladen. */}
          {/* Intranettet (beslutning 100). Opslag og begivenheder har deres egen
              side, fordi beskederne linker direkte ind til dem. */}
          <Route path="intranet" element={<IntranetPage />} />
          <Route path="receipts" element={<ReceiptsPage />} />
          <Route path="intranet/opslag/:id" element={<IntranetPostPage />} />
          <Route path="intranet/begivenheder/:id" element={<IntranetEventPage />} />
          <Route path="intranet/moeder/:id" element={<IntranetMeetingPage />} />
          <Route path="intranet/mus/:id" element={<MusPage />} />
          <Route path="intranet/laerling/:id" element={<ApprenticePage />} />
          <Route path="self-checks/:id" element={<SelfCheckPage />} />
          <Route path="manuals" element={<ManualsPage />} />
          {/* "new" foer ":id", ellers ville /manuals/new laese "new" som et id. */}
          <Route path="manuals/new" element={<ManualEditorPage />} />
          <Route path="manuals/:id" element={<ManualDetailPage />} />
          <Route path="manuals/:id/edit" element={<ManualEditorPage />} />

          {/* Henvendelser (beslutning 073). ALLE indloggede maa melde noget -
              ogsaa en montoer med et problem i systemet. Hvad man SER, styres
              i API-et. */}
          <Route path="support" element={<SupportPage />} />
          <Route path="support/:id" element={<SupportTicketPage />} />

          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="more" element={<MorePage />} />

          {/*
            Siderne her staar i mainNav og har `[Authorize(Policy = AuthPolicies.User)]`
            paa controlleren: alle indloggede maa LAESE dem. De laa foer bag `isAdmin`,
            saa menupunktet foerte tilbage til forsiden for alle andre end en
            administrator - og saa kunne den, der tog telefonen, ikke svare paa, om
            anlaegget var daekket (beslutning 059), hvem der havde noeglen (055) eller
            hvem der var paa ferie (053). Skriveknapperne inde paa siderne er fortsat
            skjult efter rolle med useAuth()'s isAdmin/isProjectManager, og backenden
            haandhaever det samme pr. endpoint.
          */}
          <Route path="absences" element={<AbsencesPage />} />
          <Route path="certificates" element={<CertificatesPage />} />
          <Route path="access-keys" element={<AccessKeysPage />} />
          <Route path="equipment" element={<EquipmentPage />} />
          <Route path="vehicles" element={<VehiclesPage />} />
          <Route path="vehicles/:id" element={<VehicleDetailPage />} />
          {/* Vaerktoejets egen side og dens QR-maerkat (beslutning 096). */}
          <Route path="equipment/:id" element={<EquipmentDetailPage />} />
          <Route path="equipment/:id/label" element={<EquipmentLabelPage />} />
          {/* Adressen QR-maerkaterne paa vaerktoej peger paa - ikke /e/ og ikke /v/. */}
          <Route path="m/:code" element={<EquipmentCodeLandingPage />} />
          <Route path="warranties" element={<WarrantiesPage />} />
          {/* Montørens egen VPN-profil (beslutning 091). Serveren lukker den, når modulet er slukket. */}
          {canUseVpn && <Route path="vpn" element={<VpnProfilePage />} />}

          {/*
            Kontorets to beskedsider kraever Projektleder - ikke Administrator.
            Menupunkterne er markeret `projectManager: true` i navigation.ts, saa
            ruterne skal foelge med, ellers ser projektlederen et punkt, der bare
            sender ham hjem igen (beslutning 042 og 060).
          */}
          {isProjectManager && (
            <>
              <Route path="field-messages" element={<FieldMessagesPage />} />
              <Route path="admin/messages" element={<MessagesPage />} />
              {/* Procedurer og skabeloner (beslutning 141). */}
              <Route path="admin/templates" element={<TemplatesPage />} />
              {/* Diagrammer paa sagen (beslutning 150). */}
              <Route path="diagrams" element={<DiagramsPage />} />
              <Route path="diagrams/:id" element={<DiagramEditorPage />} />
            </>
          )}

          {isAdmin && (
            <>
              <Route path="admin/users" element={<UsersPage />} />
              <Route path="admin/teams" element={<TeamsPage />} />
              <Route path="admin/service-checklists" element={<ServiceChecklistsPage />} />
              <Route path="admin/manual-categories" element={<ManualCategoriesPage />} />
              <Route path="admin/equipment-categories" element={<EquipmentCategoriesPage />} />
              <Route path="admin/device-types" element={<DeviceTypesPage />} />
              <Route path="admin/statuses" element={<StatusesPage />} />
              <Route path="admin/screens" element={<ScreensPage />} />
              <Route path="admin/dashboards" element={<DashboardsPage />} />
              <Route path="admin/activity" element={<ActivityPage />} />
              {/* Prisfiler (beslutning 072). Administrator: én fil roerer
                  tusind varer, og en forkert kolonne fejler ikke - den er
                  bare forkert. */}
              <Route path="admin/support-mail" element={<SupportMailPage />} />
              <Route path="admin/price-import" element={<PriceImportPage />} />
              <Route path="admin/modules" element={<ModulesPage />} />
              <Route path="admin/operations" element={<OperationsPage />} />
              <Route path="admin/deadline-alerts" element={<DeadlineAlertsPage />} />
              <Route path="admin/subscriptions" element={<SubscriptionsPage />} />
              {hasRole('Superadmin') && <Route path="admin/microsoft365" element={<Microsoft365Page />} />}
              {hasRole('Superadmin') && <Route path="admin/sms" element={<SmsPage />} />}
              {hasRole('Superadmin') && <Route path="admin/ordrestyring" element={<OrdrestyringPage />} />}
              {hasRole('Superadmin') && <Route path="admin/vpn" element={<VpnPage />} />}
              {hasRole('Superadmin') && <Route path="admin/vpn/sites/:id/label" element={<VpnLabelPage />} />}
            </>
          )}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}
